import React, { useState } from 'react';
import Icon from '../../components/AppIcon';

const ToggleSwitch = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
      checked ? 'bg-green-500' : 'bg-gray-300'
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

const SmsConsentBox = ({ type }) => {
  const isTech = type === 'technician';
  return (
    <div className="mt-4 p-4 rounded-xl border border-amber-200 bg-amber-50/70 space-y-3">
      <div className="flex items-start gap-2">
        <Icon name="ShieldCheck" size={15} color="#d97706" className="mt-0.5 shrink-0" />
        <p className="text-sm font-semibold text-amber-800">SMS Messaging Consent</p>
      </div>
      <p className="text-sm text-amber-900 leading-relaxed">
        {isTech
          ? 'By enabling SMS notifications, you consent to receive automated text messages from ServiceJockey regarding job assignments, schedule changes, and job completion alerts. Message frequency varies based on your activity — you may receive up to several messages per day.'
          : 'By enabling SMS, you consent to receive automated text messages from ServiceJockey about your service requests, appointments, invoices, and quotes. Message frequency varies based on your activity.'}
      </p>
      <ul className="text-sm text-amber-900 space-y-1.5 pl-1">
        <li className="flex items-center gap-2">
          <Icon name="Info" size={12} color="#92400e" />
          <span>Msg &amp; data rates may apply.</span>
        </li>
        <li className="flex items-center gap-2">
          <Icon name="HelpCircle" size={12} color="#92400e" />
          <span>Reply <strong>HELP</strong> for help.</span>
        </li>
        <li className="flex items-center gap-2">
          <Icon name="XCircle" size={12} color="#92400e" />
          <span>Reply <strong>STOP</strong> to opt out at any time.</span>
        </li>
      </ul>
      <p className="text-sm text-amber-800">
        {isTech
          ? 'By enabling SMS above, you expressly consent to receive these text messages. This consent is separate from any agreement to our ' :'This SMS consent is separate from our '}
        <a
          href="https://servicejockey.net/terms-and-conditions/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline font-medium hover:text-amber-900"
        >
          Terms of Service
        </a>
        {' '}and{' '}
        <a
          href="https://servicejockey.net/privacy-policy/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline font-medium hover:text-amber-900"
        >
          Privacy Policy
        </a>
        .
      </p>
    </div>
  );
};

const TechnicianOptInDemo = () => {
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [phone, setPhone] = useState('');

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/60">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center">
            <Icon name="Wrench" size={18} color="#2563eb" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Technician Opt-In</h3>
            <p className="text-xs text-gray-500 mt-0.5">My Profile → Notification Delivery</p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-5 space-y-5">
        {/* Path callout */}
        <div className="flex items-start gap-2.5 p-3 rounded-lg bg-blue-50 border border-blue-100">
          <Icon name="Navigation" size={14} color="#2563eb" className="mt-0.5 shrink-0" />
          <p className="text-xs text-blue-800 leading-relaxed">
            The technician clicks their name in the top-left header, selects <strong>My Profile</strong>, then scrolls to the <strong>Notification Delivery</strong> section.
          </p>
        </div>

        {/* SMS channel card */}
        <div className={`rounded-xl border p-4 transition-colors ${smsEnabled ? 'border-green-200 bg-green-50/40' : 'border-gray-200 bg-gray-50/40'}`}>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2.5">
              <Icon name="MessageSquare" size={16} color={smsEnabled ? '#16a34a' : '#6b7280'} />
              <span className="text-sm font-medium text-gray-800">SMS Notifications</span>
            </div>
            <ToggleSwitch checked={smsEnabled} onChange={setSmsEnabled} />
          </div>
          <p className="text-xs text-gray-500 ml-6.5">Receive text alerts for job events</p>

          {smsEnabled && (
            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Mobile Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e?.target?.value)}
                  placeholder="(555) 123-4567"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
                />
              </div>
              <SmsConsentBox type="technician" />
              <button
                type="button"
                className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Save Preferences
              </button>
            </div>
          )}
        </div>

        {!smsEnabled && (
          <p className="text-xs text-gray-400 text-center italic">Toggle SMS on to see the consent disclosure</p>
        )}
      </div>
    </div>
  );
};

const CustomerOptInDemo = () => {
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [frequency, setFrequency] = useState('immediate');

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/60">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center">
            <Icon name="User" size={18} color="#7c3aed" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Customer Opt-In</h3>
            <p className="text-xs text-gray-500 mt-0.5">Customer Portal → Communication Preferences</p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-5 space-y-5">
        {/* Path callout */}
        <div className="flex items-start gap-2.5 p-3 rounded-lg bg-purple-50 border border-purple-100">
          <Icon name="Navigation" size={14} color="#7c3aed" className="mt-0.5 shrink-0" />
          <p className="text-xs text-purple-800 leading-relaxed">
            The customer logs into the <strong>Customer Portal</strong> (or clicks a tokenized link from an email) and navigates to <strong>Communication Preferences</strong>.
          </p>
        </div>

        {/* SMS channel card */}
        <div className={`rounded-xl border p-4 transition-colors ${smsEnabled ? 'border-green-200 bg-green-50/40' : 'border-red-100 bg-red-50/30'}`}>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2.5">
              <Icon name="MessageSquare" size={16} color={smsEnabled ? '#16a34a' : '#dc2626'} />
              <span className="text-sm font-medium text-gray-800">SMS</span>
            </div>
            <ToggleSwitch checked={smsEnabled} onChange={setSmsEnabled} />
          </div>
          <p className="text-xs text-gray-500 ml-6.5">Text message notifications</p>

          {smsEnabled && (
            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Message Frequency</label>
                <select
                  value={frequency}
                  onChange={e => setFrequency(e?.target?.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
                >
                  <option value="immediate">Immediately — As soon as events happen</option>
                  <option value="daily_digest">Daily Digest — Once per day summary</option>
                  <option value="weekly_digest">Weekly Digest — Once per week summary</option>
                  <option value="none">None — No notifications</option>
                </select>
              </div>
              <SmsConsentBox type="customer" />
              <button
                type="button"
                className="w-full py-2.5 px-4 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Save Preferences
              </button>
            </div>
          )}
        </div>

        {!smsEnabled && (
          <p className="text-xs text-gray-400 text-center italic">Toggle SMS on to see the consent disclosure</p>
        )}
      </div>
    </div>
  );
};

const SmsOptInProof = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/assets/images/ServiceJockeyLogo_Signature-1782343696238.png"
              alt="ServiceJockey logo"
              className="h-8 object-contain"
              onError={e => { e.target.style.display = 'none'; }}
            />
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-200">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs font-medium text-green-700">Public Compliance Page</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 space-y-10">

        {/* Page title */}
        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-sm font-medium mb-2">
            <Icon name="ShieldCheck" size={15} color="#2563eb" />
            A2P 10DLC Opt-In Proof
          </div>
          <h1 className="text-3xl font-bold text-gray-900">SMS Opt-In Consent Forms</h1>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            This page demonstrates the SMS opt-in method used by ServiceJockey for both technicians and customers.
            Consent disclosures are displayed at the point of opt-in, before any messages are sent.
          </p>
        </div>

        {/* Compliance summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: 'CheckCircle', color: '#16a34a', bg: 'bg-green-50 border-green-200', label: 'Opt-In Method', value: 'WEB_FORM — Toggle on web form' },
            { icon: 'CheckCircle', color: '#16a34a', bg: 'bg-green-50 border-green-200', label: 'Disclosure Timing', value: 'Shown at point of opt-in only' },
            { icon: 'CheckCircle', color: '#16a34a', bg: 'bg-green-50 border-green-200', label: 'Opt-Out Method', value: 'Reply STOP · Toggle off in settings' },
          ]?.map(item => (
            <div key={item?.label} className={`flex items-start gap-3 p-4 rounded-xl border ${item?.bg}`}>
              <Icon name={item?.icon} size={18} color={item?.color} className="mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-gray-700">{item?.label}</p>
                <p className="text-xs text-gray-600 mt-0.5">{item?.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Live interactive demos */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 mb-1">Interactive Opt-In Forms</h2>
          <p className="text-sm text-gray-500 mb-5">
            Toggle the SMS switch on each form below to see the exact consent disclosure presented to users.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TechnicianOptInDemo />
            <CustomerOptInDemo />
          </div>
        </div>

        {/* Flow descriptions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Technician flow */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-4">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <Icon name="Wrench" size={16} color="#2563eb" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">Technician Opt-In Flow</h3>
            </div>
            <ol className="space-y-3">
              {[
                'Technician logs into the ServiceJockey web app and clicks their name in the top-left header.',
                'Selects "My Profile" from the dropdown menu.',
                'Scrolls to the "Notification Delivery" section on their profile page.',
                'Clicks the SMS Notifications toggle — the toggle flips to enabled.',
                'A phone number input field and the amber SMS Messaging Consent disclosure box appear immediately.',
                'Technician reads the disclosure, enters or confirms their mobile number, and clicks "Save Preferences".',
                'Consent is recorded in the database (user_notification_preferences) with sms_enabled: true.',
              ]?.map((step, i) => (
                <li key={i} className="flex gap-3 text-sm text-gray-700">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                  <span className="leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Customer flow */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-4">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                <Icon name="User" size={16} color="#7c3aed" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">Customer Opt-In Flow</h3>
            </div>
            <ol className="space-y-3">
              {[
                'Customer logs into the ServiceJockey Customer Portal, or clicks a tokenized link sent via email.',
                'Navigates to "Communication Preferences" in the portal navigation.',
                'The page shows two channel cards: SMS (disabled/red) and Email.',
                'Customer clicks the toggle on the SMS card — the card switches to the active/green state.',
                'A frequency dropdown and the amber A2P 10DLC consent disclosure box appear immediately.',
                'Customer reads the disclosure, selects their preferred message frequency, and clicks "Save Preferences".',
                'Consent is recorded in the database (customer_comm_preferences) with sms_enabled: true.',
              ]?.map((step, i) => (
                <li key={i} className="flex gap-3 text-sm text-gray-700">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                  <span className="leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Full disclosure text */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-6">
          <h2 className="text-base font-semibold text-gray-900">Full Consent Disclosure Text</h2>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-blue-700 flex items-center gap-2">
              <Icon name="Wrench" size={13} color="#2563eb" /> Technician Disclosure
            </h4>
            <div className="p-4 rounded-xl border border-amber-200 bg-amber-50/60 space-y-2 text-sm text-amber-900">
              <p className="leading-relaxed font-medium">SMS Messaging Consent</p>
              <p className="leading-relaxed">By enabling SMS notifications, you consent to receive automated text messages from ServiceJockey regarding job assignments, schedule changes, and job completion alerts. Message frequency varies based on your activity — you may receive up to several messages per day.</p>
              <ul className="space-y-1 pl-1">
                <li>• Msg &amp; data rates may apply.</li>
                <li>• Reply HELP for help.</li>
                <li>• Reply STOP to opt out at any time.</li>
              </ul>
              <p>By enabling SMS above, you expressly consent to receive these text messages. This consent is separate from any agreement to our <a href="https://servicejockey.net/terms-and-conditions/" target="_blank" rel="noopener noreferrer" className="underline font-medium">Terms of Service</a> and <a href="https://servicejockey.net/privacy-policy/" target="_blank" rel="noopener noreferrer" className="underline font-medium">Privacy Policy</a>.</p>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-purple-700 flex items-center gap-2">
              <Icon name="User" size={13} color="#7c3aed" /> Customer Disclosure
            </h4>
            <div className="p-4 rounded-xl border border-amber-200 bg-amber-50/60 space-y-2 text-sm text-amber-900">
              <p className="leading-relaxed">By enabling SMS, you consent to receive automated text messages from ServiceJockey about your service requests, appointments, invoices, and quotes. Message frequency varies based on your activity.</p>
              <ul className="space-y-1 pl-1">
                <li>• Msg &amp; data rates may apply.</li>
                <li>• Reply HELP for help.</li>
                <li>• Reply STOP to opt out at any time.</li>
              </ul>
              <p>This SMS consent is separate from our <a href="https://servicejockey.net/terms-and-conditions/" target="_blank" rel="noopener noreferrer" className="underline font-medium">Terms of Service</a> and <a href="https://servicejockey.net/privacy-policy/" target="_blank" rel="noopener noreferrer" className="underline font-medium">Privacy Policy</a>.</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center py-6 border-t border-gray-200 space-y-1">
          <p className="text-sm text-gray-500">
            ServiceJockey · <a href="https://servicejockey.net/privacy-policy/" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700">Privacy Policy</a> · <a href="https://servicejockey.net/terms-and-conditions/" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700">Terms of Service</a>
          </p>
          <p className="text-xs text-gray-400">This page is publicly accessible for A2P 10DLC compliance verification.</p>
        </div>
      </div>
    </div>
  );
};

export default SmsOptInProof;
